import { createColumnHelper } from "@tanstack/react-table";
import { T } from "@tolgee/react";
import { KrwSubtext } from "#/components/krw-subtext";
import {
	formatPrice,
	formatSignedPercent,
	formatSignedUsd,
	formatSize,
	formatUsd,
	toNumber,
} from "../../lib/formatters.ts";
import { CloseAllHeader } from "./close-all-header.tsx";
import { PositionCloseCell } from "./position-close-cell.tsx";
import { PositionTpslCell } from "./position-tpsl-cell.tsx";
import type {
	PositionRowItem,
	PositionsTableMeta,
} from "./types/positions.types.ts";

const col = createColumnHelper<PositionRowItem>();

function PnlCell({ pnl, roe }: { pnl: number; roe: number }) {
	const sign = pnl > 0 ? "+" : "";
	return (
		<div className={pnl >= 0 ? "text-trade-green" : "text-trade-red"}>
			<div className="text-label2-tabular-medium">
				{sign}
				{formatSize(Math.abs(pnl), 2)} USDC
			</div>
			<KrwSubtext usd={pnl} inheritColor parens />
			<div className="text-caption1 opacity-70">
				{formatSignedPercent(roe * 100)}
			</div>
		</div>
	);
}

export function createPositionsColumns() {
	return [
		// Market (Coin + leverage)
		col.accessor("coin", {
			header: () => <T keyName="positions.columns.coin" />,
			cell: ({ getValue, row, table }) => {
				const meta = table.options.meta as PositionsTableMeta;
				const isLong = toNumber(row.original.szi) >= 0;
				const lev = row.original.leverage;
				const levLabel = `${toNumber(lev.value)}x`;
				return (
					<div
						className={`border-l-2 pl-2 ${isLong ? "border-trade-green" : "border-trade-red"}`}
					>
						<button
							type="button"
							onClick={() => meta.onNavigate(getValue())}
							className="cursor-pointer text-label2-medium text-trade-text-strong hover:underline"
						>
							{getValue()}
						</button>
						<span className="ml-1 text-caption1 text-trade-text-faint">
							{levLabel}
						</span>
					</div>
				);
			},
		}),

		// Size
		col.accessor((row) => toNumber(row.szi), {
			id: "positionSize",
			header: () => <T keyName="positions.columns.size" />,
			cell: ({ row }) => (
				<span className="text-label2-tabular-medium text-trade-text-strong">
					{formatSize(Math.abs(toNumber(row.original.szi)))} {row.original.coin}
				</span>
			),
		}),

		// Position Value
		col.accessor((row) => toNumber(row.positionValue), {
			id: "positionValue",
			header: () => <T keyName="positions.columns.position-value" />,
			cell: ({ row }) => (
				<span className="text-label2-tabular-medium text-trade-text-strong">
					{formatUsd(row.original.positionValue)}
				</span>
			),
		}),

		// Entry Price
		col.accessor((row) => toNumber(row.entryPx), {
			id: "entryPx",
			header: () => <T keyName="positions.columns.entry-price" />,
			cell: ({ row }) => (
				<span className="text-label2-tabular-medium text-trade-text-strong">
					{formatPrice(row.original.entryPx)}
				</span>
			),
		}),

		// Mark Price
		col.accessor((row) => toNumber(row.markPx), {
			id: "markPx",
			header: () => <T keyName="positions.columns.mark-price" />,
			cell: ({ row }) => (
				<span className="text-label2-tabular-medium text-trade-text-strong">
					{row.original.markPx ? formatPrice(row.original.markPx) : "N/A"}
				</span>
			),
		}),

		// PnL (ROE %)
		col.accessor((row) => toNumber(row.unrealizedPnl), {
			id: "pnl",
			header: () => <T keyName="positions.columns.pnl" />,
			cell: ({ row }) => {
				const pnl = toNumber(row.original.unrealizedPnl);
				return (
					<PnlCell pnl={pnl} roe={toNumber(row.original.returnOnEquity)} />
				);
			},
		}),

		// Liq. Price
		col.accessor((row) => toNumber(row.liquidationPx), {
			id: "liquidationPx",
			header: () => <T keyName="positions.columns.liq-price" />,
			cell: ({ row }) => (
				<span className="text-label2-tabular-medium text-[var(--color-theme-denotives-yellow)] dark:text-[var(--color-theme-denotives-yellow)]">
					{row.original.liquidationPx
						? formatPrice(row.original.liquidationPx)
						: "N/A"}
				</span>
			),
		}),

		// Margin
		col.accessor((row) => toNumber(row.marginUsed), {
			id: "margin",
			header: () => <T keyName="positions.columns.margin" />,
			cell: ({ row }) => {
				const leverageTypeKey =
					row.original.leverage.type === "isolated"
						? "margin.isolated-label"
						: "margin.cross-label";
				return (
					<div className="text-trade-text-strong">
						<span className="text-label2-tabular-medium">
							{formatUsd(row.original.marginUsed)}
						</span>
						<span className="ml-1 text-caption1 text-trade-text-faint">
							(<T keyName={leverageTypeKey} />)
						</span>
					</div>
				);
			},
		}),

		// Funding
		col.accessor((row) => -toNumber(row.cumFunding?.sinceOpen), {
			id: "funding",
			header: () => <T keyName="positions.columns.funding" />,
			cell: ({ row }) => {
				const funding = -toNumber(row.original.cumFunding?.sinceOpen);
				return (
					<span
						className={`text-label2-tabular-medium ${funding >= 0 ? "text-trade-green" : "text-trade-red"}`}
					>
						{formatSignedUsd(funding)}
					</span>
				);
			},
		}),

		// Close All
		col.display({
			id: "close",
			header: ({ table }) => {
				const meta = table.options.meta as PositionsTableMeta;
				return <CloseAllHeader onConfirm={meta.onCloseAll} />;
			},
			enableSorting: false,
			cell: ({ row, table }) => {
				const meta = table.options.meta as PositionsTableMeta;
				return (
					<PositionCloseCell
						position={row.original}
						onCloseMarket={meta.onCloseMarket}
						onCloseLimitSubmit={meta.onCloseLimitSubmit}
						onReverse={meta.onReverse}
					/>
				);
			},
		}),

		// TP/SL
		col.display({
			id: "tpsl",
			header: () => <span className={"h-full flex items-center"}>TP/SL</span>,
			enableSorting: false,
			cell: ({ row, table }) => {
				const meta = table.options.meta as PositionsTableMeta;
				const coin = row.original.coin;
				const szi = toNumber(row.original.szi);
				const isLong = szi >= 0;
				const closeSide = isLong ? "A" : "B";

				const triggerOrders = meta.openOrders.filter(
					(o) =>
						o.coin === coin &&
						o.reduceOnly &&
						o.side === closeSide &&
						toNumber(o.triggerPx) > 0,
				);

				const tpOrder = triggerOrders.find(
					(o) =>
						o.orderType === "Take Profit Market" ||
						o.orderType === "Take Profit Limit",
				);
				const slOrder = triggerOrders.find(
					(o) => o.orderType === "Stop Market" || o.orderType === "Stop Limit",
				);

				return (
					<PositionTpslCell
						position={row.original}
						tpOrder={tpOrder}
						slOrder={slOrder}
						isLong={isLong}
						szDecimals={meta.szDecimalsByCoin[coin] ?? 4}
						onSetTpsl={meta.onSetTpsl}
						onCancelOrder={meta.onCancelOrder}
					/>
				);
			},
		}),
	];
}
